import React, { useState } from "react";
import Dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

// Custom.
import Layout from "containers/Layout/Layout";
import { Form } from "@vocollege/components";
import I18n from "@vocollege/app/dist/modules/Services/I18n";
import {
  GET_ARTICLE,
  UPDATE_ARTICLE,
  CREATE_ARTICLE,
} from "@vocollege/app/dist/modules/VoApi/graphql";
import { VoAuth } from "@vocollege/app";
import Utils from "services/Utils";

export default function ArticleEdit() {
  const router = useParams<any>();
  const initialDate = Dayjs().format("YYYY-MM-DD");
  const [urlValue, setUrlValue] = useState("");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(initialDate);
  const navigate = useNavigate();

  // Methods.

  const getInitialState = () => {
    const initialState: { [key: string]: any } = {
      id: 0,
      title: "",
      body: "",
      status: 0,
      url: "",
      author: null,
      date: initialDate,
      tags: [],
      images: [],
      attachments: [],
      settings: [],
    };
    const { id } = router;
    if (id && id === "create") {
      initialState.author = {
        id: VoAuth.currentUser.id,
        name: VoAuth.currentUser.name,
        type: "user",
      };
    }
    return initialState;
  };
  const handleTitleChange = (data: any) => {
    setTitle(data);
    updateUrlValue(date, data);
  };

  const handleDateChange = (data: any) => {
    setDate(data);
    updateUrlValue(data, title);
  };

  const updateUrlValue = (date: string, title: string) => {
    setUrlValue(`nyhet/${date.replaceAll("-", "/")}/${title}`);
  };

  const handleRedirect = (state: any) => {
    if (Utils.redirect(state)) {
      toast.info(I18n.get.form.messages.redirecting, { autoClose: 0 });
    } else {
      navigate("/content/articles");
    }
  };

  return (
    <Layout>
      <Form
        tabs={[
          {
            key: "content",
            label: I18n.get.misc.content,
            fields: [
              {
                name: "title",
                label: I18n.get.form.labels.title,
                type: "text",
                required: true,
                grid: {
                  xs: 12,
                  md: 9,
                },
                validation: {
                  required: true,
                },
                onChange: handleTitleChange,
              },
              {
                name: "status",
                label: I18n.get.status.published,
                type: "switch",
                required: true,
                grid: {
                  xs: 12,
                  md: 3,
                },
              },
              {
                name: "images",
                label: I18n.get.misc.image,
                type: "file_field",
                grid: {
                  xs: 12,
                },
                params: {
                  filetypes: ["image"],
                  multiple: false,
                },
              },
              {
                name: "settings",
                label: I18n.get.misc.settings,
                type: "checkboxes",
                grid: {
                  xs: 12,
                },
                params: {
                  availableValues: I18n.get.article.settings,
                },
              },
              {
                name: "body",
                label: I18n.get.form.labels.body,
                type: "editor",
                required: false,
                grid: {
                  xs: 12,
                },
              },
            ],
          },
          {
            key: "extra",
            label: I18n.get.misc.extra,
            fields: [
              {
                name: "url",
                label: I18n.get.form.labels.url,
                type: "url_field",
                required: true,
                grid: {
                  xs: 12,
                },
                validation: {
                  required: true,
                },
                overrideValue: urlValue,
                params: {
                  dependencyHelperText: I18n.trans(
                    I18n.get.form.messages.fieldPopulatedBy,
                    {
                      field: `${I18n.get.form.labels.title}/${I18n.get.misc.date}`,
                    }
                  ),
                },
              },
              {
                name: "tags",
                label: I18n.get.tag.labels.tags,
                type: "tags_field",
                grid: {
                  xs: 12,
                },
                params: {
                  dialog: {
                    types: ["tag"],
                    addNew: true,
                  },
                },
              },
              {
                name: "date",
                label: I18n.get.misc.date,
                type: "date_field",
                required: true,
                grid: {
                  xs: 12,
                  md: 4,
                },
                validation: {
                  required: true,
                },
                onChange: handleDateChange,
              },
              {
                name: "author",
                label: I18n.get.misc.author,
                type: "entity_field",
                grid: {
                  xs: 12,
                  md: 8,
                },
                params: {
                  dialog: {
                    types: ["user"],
                  },
                  fields: {
                    user: {
                      title: "name",
                    },
                  },
                },
              },
            ],
          },
          {
            key: "attachments",
            label: I18n.get.misc.attachments,
            fields: [
              {
                name: "attachments",
                label: I18n.get.misc.attachments,
                type: "content_list",
                grid: {
                  xs: 12,
                },
                params: {
                  contentType: "file",
                },
              },
            ],
          },
        ]}
        labels={{
          loading: I18n.get.form.labels.loadingContent,
          create: I18n.get.form.labels.createArticle,
          created: I18n.get.article.messages.articleSaved,
          updated: I18n.get.article.messages.articleSaved,
          deleted: I18n.get.article.messages.articleDeleted,
          fieldRequired: I18n.get.form.errors.required,
        }}
        operations={{
          category: "article",
          get: GET_ARTICLE,
          create: CREATE_ARTICLE,
          update: UPDATE_ARTICLE,
        }}
        // paths={{
        //   back: "/content/articles",
        // }}
        initialState={getInitialState()}
        pageTitleField="title"
        classes={{
          paper: "vo-global__content-with-toolbar",
          toolbar: "vo-global__content-toolbar",
        }}
        onComplete={handleRedirect}
        onCancel={handleRedirect}
      />
    </Layout>
  );
}
